/**
 * Tipos y llamadas REALES de `consola/tarifario` (`apps/api/src/tarifario/tarifario.controller.ts`).
 * Mismo criterio que `verificacionApi.ts` e `incidentesApi.ts`: los tipos salen de leer el backend
 * (controller + service + DTOs + `schema.prisma`), NUNCA del mock.
 *
 * ✅ **Acá SÍ se reusa `TipoVehiculo` del contrato compartido, y eso es una diferencia REAL con
 * Verificación e Incidentes — no un descuido.** Los otros dos archivos declaran sus propios enums
 * porque los del mock NO coinciden con el backend. Este caso se midió antes de decidir:
 * `enum TipoVehiculo { AUTO }` en `apps/api/prisma/schema.prisma` contra
 * `export type TipoVehiculo = 'AUTO'` en `packages/contratos/src/dominio.ts` —
 * **coinciden exactamente**, así que redeclararlos acá crearía una tercera copia del mismo enum
 * sin ganar nada, y `TIPO_VEHICULO` de `componentes/Etiqueta.tsx` (indexado por ese tipo) sirve
 * tal cual. Es la única de las tres pantallas donde reusar es lo correcto.
 *
 * 📌 **Los dos tenían además `MOTOTAXI` hasta el 2026-09-01**, y seguían coincidiendo; la clase
 * se retiró por decisión del owner (el servicio es sólo vehículos taxi) y los dos se movieron
 * juntos — que es exactamente lo que esta nota predecía al reusar en vez de copiar.
 *
 * 🔴 **`Zona` real NO tiene `recargoPorcentaje`.** El mock (`dominio/contratos.ts`) lo declara y
 * `Tarifario.tsx` lo mostraba como columna; `model Zona` en el schema tiene
 * `id · nombre · poligono · activa · createdAt · updatedAt` y nada más — el propio
 * `TarifarioService.zonas()` lo dice: *"no hay `recargoPorcentaje` en `Zona` (el schema no lo
 * modela)"*. Esa columna mostraba un número que no existe en ningún lado. No se migró: se retiró.
 *
 * ⚠️ **Los `Decimal` de Prisma llegan como STRING sobre HTTP** (`"12.50"`, no `12.5`) — el propio
 * `tarifario.controller.ts` lo advierte en su cabecera y el test de integración lo prueba
 * (`expect(Number(res.body[0].banderazo)).toBe(4.5)`). Son SEIS campos acá
 * (`banderazo`/`porKm`/`porMinuto`/`minimo`/`factorMin`/`factorMax`), no uno. Se tipan `string` y
 * se pasan por `aNumero()` antes de formatear o comparar.
 *
 * ⚠️ **Y en la dirección de ida es al revés: el POST tiene que mandar NÚMEROS.** El
 * `ValidationPipe` global de `app.module.ts` corre con `transform: true` pero **sin**
 * `enableImplicitConversion` ⇒ `@IsNumber()` sobre un `"5.00"` de un `<input>` da **400**. Por eso
 * `PublicacionTarifario` declara `number` y el formulario convierte antes de llamar.
 */
import { ErrorApi, obtener, postear } from './clienteApi';
import type { TipoVehiculo } from './contratos';

// ---------------------------------------------------------------------------
// Filas tal cual serializan por HTTP
// ---------------------------------------------------------------------------

/**
 * `Tarifario` de Prisma. Los seis campos monetarios/factor son STRING (ver la cabecera).
 * Sin `editadoPorId` ni `motivo`: el modelo NO los tiene — quién publicó queda sólo en el log de
 * aplicación del backend (`TarifarioService.publicar`), no en una columna. Es una brecha conocida
 * frente a CA-10.2, documentada en `publicar-tarifario.dto.ts`; la pantalla la dice en voz alta en
 * vez de disimularla pidiendo un motivo que no se guardaría en ningún lado.
 */
export interface TarifarioReal {
  id: string;
  version: number;
  tipoVehiculo: TipoVehiculo;
  /** `null` = tarifario GLOBAL, que es el único que `TarifaService.calcular()` lee. */
  zonaId: string | null;
  /** ISO-4217, `Char(3)`. */
  moneda: string;
  banderazo: string;
  porKm: string;
  porMinuto: string;
  minimo: string;
  factorMin: string;
  factorMax: string;
  vigenteDesde: string;
  /** `null` = ESTA es la fila vigente. El `GET` sólo devuelve filas con `null`. */
  vigenteHasta: string | null;
  createdAt: string;
}

/**
 * `Zona` de Prisma. `poligono` es `Json` y esta pantalla NO lo dibuja (hace falta el servidor de
 * teselas) — se tipa `unknown` a propósito: darle una forma inventada sería afirmar algo que no
 * se midió.
 */
export interface ZonaReal {
  id: string;
  nombre: string;
  poligono: unknown;
  activa: boolean;
  createdAt: string;
  updatedAt: string;
}

// ---------------------------------------------------------------------------
// Topes — ESPEJO de publicar-tarifario.dto.ts, no una preferencia de esta pantalla
// ---------------------------------------------------------------------------

/**
 * `Decimal(10, 2)` en `schema.prisma` ⇒ 99.999.999,99 es lo máximo que la columna admite.
 * El DTO lo hace cumplir con `@Max`; acá se repite para que el operador lo vea ANTES de mandar.
 *
 * ⚠️ Es una COPIA de un valor que vive en `apps/api`: si allá cambia la precisión de la columna,
 * esto queda stale y el formulario deja pasar algo que el backend rechaza. El modo de falla es
 * benigno (400 con el mensaje del backend, que la pantalla muestra), no un dato malo guardado.
 */
export const MAX_MONTO = 99_999_999.99;

/** `Decimal(4, 2)` para `factorMin`/`factorMax` ⇒ 99,99. Mismo espejo, mismo motivo. */
export const MAX_FACTOR = 99.99;

/** Los `Decimal` guardan 2 decimales: un tercer decimal lo redondea Postgres, sin avisar. */
export const DECIMALES = 2;

// ---------------------------------------------------------------------------
// GET /consola/tarifario  ·  GET /consola/tarifario/zonas
// ---------------------------------------------------------------------------

/**
 * Sólo las filas VIGENTES (`vigenteHasta: null`) — el servicio no expone el histórico. Sin
 * `tipoVehiculo` trae todas las clases, que es lo que esta pantalla necesita; el filtro existe en
 * el backend y no se usa acá (con dos clases, filtrar del lado del cliente no cuesta una llamada).
 */
export const obtenerTarifariosVigentes = (tipoVehiculo?: TipoVehiculo): Promise<TarifarioReal[]> =>
  obtener<TarifarioReal[]>(
    `/consola/tarifario${tipoVehiculo ? `?tipoVehiculo=${tipoVehiculo}` : ''}`,
  );

/** Todas las zonas, activas e inactivas, ordenadas por nombre. Lectura simple: no hay CRUD de zonas. */
export const obtenerZonas = (): Promise<ZonaReal[]> =>
  obtener<ZonaReal[]>('/consola/tarifario/zonas');

// ---------------------------------------------------------------------------
// POST /consola/tarifario
// ---------------------------------------------------------------------------

/**
 * Cuerpo de `PublicarTarifarioDto`. **Números, no strings** (ver la cabecera).
 *
 * `zonaId` se OMITE para el tarifario global — el DTO documenta "ausente = global". Mandar
 * `zonaId: null` también pasaría (`@IsOptional()` se abstiene con `null`), pero omitirlo es lo que
 * el DTO describe y evita depender de ese detalle de `class-validator`.
 */
export interface PublicacionTarifario {
  tipoVehiculo: TipoVehiculo;
  zonaId?: string;
  moneda: string;
  banderazo: number;
  porKm: number;
  porMinuto: number;
  minimo: number;
  factorMin: number;
  factorMax: number;
}

/**
 * Publica una versión NUEVA: cierra la vigente de esa `(clase, zona)` y crea la sucesora. Nunca
 * edita la vigente — un viaje ya solicitado cita `Viaje.tarifarioId` y vería cambiar sus números
 * retroactivamente.
 *
 * 🔴 **Puede devolver 409 y NO es un error de la pantalla ni del operador.** Dos publicaciones
 * solapadas sobre la misma `(clase, zona)` leen el mismo `anterior` bajo READ COMMITTED; el índice
 * único PARCIAL `tarifarios_vigente_unico_key` (migración `20260831020000`) deja pasar UNA y
 * rechaza la otra, y el servicio traduce esa violación a 409. Un doble clic alcanza para
 * producirlo. `esPublicacionConcurrente()` lo distingue; quien llame tiene que RECARGAR y volver a
 * decidir, no reintentar a ciegas: el estado vigente ya no es el que se leyó al abrir el formulario.
 *
 * Otras respuestas de falla, todas con mensaje útil del backend en `ErrorApi.message`:
 *   · **400** — validación del DTO (`factorMin > factorMax`, tope `@Max`, moneda de largo ≠ 3,
 *     campo faltante) **o** zona INACTIVA (`BadRequestException` del servicio).
 *   · **404** — `zonaId` con forma de UUID válida pero que no existe.
 *   · **403** — token sin `esAdminPlataforma` (`OperadorGuard`).
 */
export const publicarTarifario = (entrada: PublicacionTarifario): Promise<TarifarioReal> =>
  postear<TarifarioReal>('/consola/tarifario', entrada);

/** El 409 de la carrera de publicación. Ver `publicarTarifario`. */
export const esPublicacionConcurrente = (error: unknown): boolean =>
  error instanceof ErrorApi && error.status === 409;

/** La zona desapareció entre que se cargó el formulario y se mandó (404 del servicio). */
export const esZonaInexistente = (error: unknown): boolean =>
  error instanceof ErrorApi && error.status === 404;

// ---------------------------------------------------------------------------
// Utilidades de lectura
// ---------------------------------------------------------------------------

/**
 * `Decimal` de Prisma → `number`. Devuelve `null` en vez de `NaN` cuando el valor no es un número
 * finito: `NaN` propaga en silencio a través de `Intl.NumberFormat` y termina como `NaN` en
 * pantalla, mientras que un `null` la pantalla ya sabe mostrarlo como `—`.
 */
export const aNumero = (valor: string | null): number | null => {
  if (valor === null) return null;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
};
